import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Divider,
    Chip,
    Paper,
    IconButton,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    TextField,
    Alert,
} from '@mui/material';
import {
    Description,
    Download,
    Visibility,
    Star,
    Person,
    CalendarToday,
    Article,
    Business,
    Delete,
    Edit,
    Save,
    Close,
} from '@mui/icons-material';
import StorageIcon from '@mui/icons-material/Storage';
import { useAuth } from '../authentication/AuthContext';
import axiosInstance from '../authentication/AuthContext';
import { useAuthenticatedImage } from '../authentication/hooks/AuthenticatedImage';
import SideMenu from '../components/navigation/SideMenu';
import AppNavbar from '../components/navigation/AppNavbar';
import Header from '../components/header/Header';
import { getStatusLabel, isNonTerminalStatus } from '../utils/documentStatus';

const DOCUMENT_STATUS_POLL_INTERVAL_MS = 3000;

function normalizeDepartmentsResponse(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.departments)) return data.departments;
    return [];
}

function normalizeDocumentsResponse(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.documents)) return data.documents;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.data)) return data.data;
    return [];
}

function DocumentThumbnail({ document, size = 40, sx = {} }) {
    const src = useAuthenticatedImage(document?.id);
    const alt = document?.title || document?.filename || 'Document thumbnail';

    if (src) {
        return (
            <Box
                component="img"
                src={src}
                alt={alt}
                loading="lazy"
                sx={{
                    width: size,
                    height: size,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                    ...sx,
                }}
            />
        );
    }

    return (
        <Description
            sx={{
                fontSize: size * 0.9,
                color: 'primary.main',
                flexShrink: 0,
                ...sx,
            }}
        />
    );
}

function DocumentStatusChip({ status, errorMessage, size = 'small' }) {
    if (!status || status === 'ingested') return null;

    if (status === 'failed') {
        return (
            <Tooltip title={errorMessage || 'Document processing failed'}>
                <Chip
                    label="Failed"
                    color="error"
                    size={size}
                    variant="filled"
                />
            </Tooltip>
        );
    }

    return (
        <Chip
            icon={
                <CircularProgress
                    size={12}
                    thickness={6}
                    sx={{ color: 'inherit', ml: 1 }}
                />
            }
            label={getStatusLabel(status)}
            color="info"
            size={size}
            variant="filled"
        />
    );
}

function DocumentPreview({ document }) {
    const src = useAuthenticatedImage(document?.id);
    const alt = document?.title || document?.filename || 'Document preview';

    if (src) {
        return (
            <Box
                component="img"
                src={src}
                alt={alt}
                sx={{
                    maxWidth: '100%',
                    maxHeight: 400,
                    objectFit: 'contain',
                }}
            />
        );
    }

    return (
        <>
            <Description
                sx={{
                    fontSize: 60,
                    color: 'grey.400',
                }}
            />
            <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
            >
                Document preview not available
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
            >
                Click the view button above to open the document in a new tab
            </Typography>
        </>
    );
}

export default function DocumentManager() {
    const { accessToken } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    const [titleError, setTitleError] = useState(null);
    const [availableDepartments, setAvailableDepartments] = useState([]);

    // Documents often only carry a department id (or a bare list of ids),
    // so fetch the full department list once to resolve ids -> names for
    // display in the detail view.
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await axiosInstance.get(
                    '/accounts/departments/'
                );
                setAvailableDepartments(
                    normalizeDepartmentsResponse(response.data)
                );
            } catch (err) {
                console.error('Failed to fetch departments:', err);
            }
        };

        if (accessToken) {
            fetchDepartments();
        }
    }, [accessToken]);

    // Reset any in-progress title edit whenever the selected document
    // changes (e.g. the user picks a different document from the list).
    useEffect(() => {
        setIsEditingTitle(false);
        setTitleDraft('');
        setTitleError(null);
    }, [selectedDocument?.id]);

    // Fetch documents from API
    useEffect(() => {
        const fetchDocuments = async () => {
            try {
                setLoading(true);
                const response = await axiosInstance.get('/documents/list/');
                const documentsData = normalizeDocumentsResponse(response.data);

                setDocuments(documentsData);

                // Auto-select first document if available
                if (documentsData.length > 0) {
                    setSelectedDocument(documentsData[0]);
                }
            } catch (err) {
                console.error('Failed to fetch documents:', err);
                setError('Failed to load documents');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken) {
            fetchDocuments();
        }
    }, [accessToken]);

    // While any document is still saving/embedding, periodically refresh
    // the list so the "in progress" indicators update without a manual
    // reload. Stops automatically once every document reaches a terminal
    // status (completed/failed).
    useEffect(() => {
        const hasNonTerminalDocument = documents.some((doc) =>
            isNonTerminalStatus(doc.status)
        );
        if (!accessToken || !hasNonTerminalDocument) return;

        const intervalId = setInterval(async () => {
            try {
                const response = await axiosInstance.get('/documents/list/');
                const documentsData = normalizeDocumentsResponse(response.data);

                setDocuments(documentsData);
                setSelectedDocument((prev) => {
                    if (!prev) return prev;
                    const updated = documentsData.find(
                        (doc) => doc.id === prev.id
                    );
                    return updated || prev;
                });
            } catch (err) {
                console.error('Failed to refresh document statuses:', err);
            }
        }, DOCUMENT_STATUS_POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [documents, accessToken]);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getFileTypeColor = (fileType) => {
        const type = fileType?.toLowerCase() || '';
        if (type.includes('pdf')) return 'error';
        if (type.includes('doc') || type.includes('txt')) return 'primary';
        if (
            type.includes('image') ||
            type.includes('jpg') ||
            type.includes('png')
        )
            return 'success';
        if (type.includes('excel') || type.includes('csv')) return 'warning';
        return 'default';
    };

    const handleDocumentSelect = (document) => {
        setSelectedDocument(document);
    };

    const resolveDepartmentById = (id) =>
        availableDepartments.find((dept) => String(dept.id) === String(id));

    const getDepartmentNames = (document) => {
        const raw = document?.departments ?? document?.department;
        if (raw === null || raw === undefined || raw === '') return [];
        const list = Array.isArray(raw) ? raw : [raw];
        return list
            .map((dept) => {
                if (typeof dept === 'string' || typeof dept === 'number') {
                    // Bare id (or already-a-name string) - try to resolve
                    // it against the fetched department list first.
                    const resolved = resolveDepartmentById(dept);
                    return resolved?.name || String(dept);
                }

                const name = dept?.name || dept?.department_name || '';
                if (name) return name;

                // Object only carries an id (e.g. { id: 3 }) - resolve it.
                const id = dept?.id ?? dept?.department_id;
                return id != null ? resolveDepartmentById(id)?.name || '' : '';
            })
            .filter(Boolean);
    };

    const handleDownload = async () => {
        if (!selectedDocument || downloading) return;

        try {
            setDownloading(true);
            const response = await axiosInstance.get(
                `/documents/${selectedDocument.id}/download/`,
                { responseType: 'blob' }
            );

            const blobUrl = window.URL.createObjectURL(
                new Blob([response.data])
            );
            const link = window.document.createElement('a');
            link.href = blobUrl;
            link.download =
                selectedDocument.filename ||
                selectedDocument.title ||
                `document-${selectedDocument.id}`;
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Failed to download document:', err);
        } finally {
            setDownloading(false);
        }
    };

    const handleDeleteDocument = async () => {
        if (!selectedDocument) return;

        try {
            setDeleting(true);
            setDeleteError(null);
            await axiosInstance.delete(
                `/documents/${selectedDocument.id}/delete/`
            );

            const deletedId = selectedDocument.id;
            setDocuments((prev) => {
                const remaining = prev.filter((doc) => doc.id !== deletedId);
                setSelectedDocument(remaining[0] || null);
                return remaining;
            });
            setDeleteDialogOpen(false);
        } catch (err) {
            console.error('Failed to delete document:', err);
            setDeleteError('Failed to delete document. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleStartEditTitle = () => {
        if (!selectedDocument) return;
        setTitleDraft(
            selectedDocument.title || selectedDocument.filename || ''
        );
        setTitleError(null);
        setIsEditingTitle(true);
    };

    const handleCancelEditTitle = () => {
        setIsEditingTitle(false);
        setTitleError(null);
    };

    const handleSaveTitle = async () => {
        if (!selectedDocument) return;

        const trimmedTitle = titleDraft.trim();
        if (!trimmedTitle) {
            setTitleError('Title cannot be empty.');
            return;
        }

        try {
            setSavingTitle(true);
            setTitleError(null);
            const response = await axiosInstance.patch(
                `/documents/${selectedDocument.id}/rename/`,
                { title: trimmedTitle }
            );

            const updatedTitle = response.data?.title ?? trimmedTitle;
            const documentId = selectedDocument.id;

            setSelectedDocument((prev) =>
                prev ? { ...prev, title: updatedTitle } : prev
            );
            setDocuments((prev) =>
                prev.map((doc) =>
                    doc.id === documentId
                        ? { ...doc, title: updatedTitle }
                        : doc
                )
            );
            setIsEditingTitle(false);
        } catch (err) {
            console.error('Failed to rename document:', err);
            setTitleError(
                err.response?.data?.error ||
                    err.response?.data?.title?.[0] ||
                    'Failed to rename document. Please try again.'
            );
        } finally {
            setSavingTitle(false);
        }
    };

    return (
        <Box sx={{ display: 'flex' }}>
            <SideMenu />
            <AppNavbar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    bgcolor: 'background.default',
                }}
            >
                <Header />
                <Box
                    sx={{
                        flex: 1,
                        width: '100%',
                        maxWidth: '100%',
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 3 },
                        pb: 5,
                        mt: { xs: 8, md: 0 },
                    }}
                >
                    <Grid
                        container
                        spacing={3}
                        sx={{ height: 'calc(100vh - 120px)' }}
                    >
                        {/* Document List Panel */}
                        <Grid item xs={12} md={4}>
                            <Paper
                                sx={{
                                    height: '100%',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <Box
                                    sx={{
                                        p: 2,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <Description />
                                        Documents ({documents.length})
                                    </Typography>
                                </Box>

                                <Box sx={{ flex: 1, overflow: 'auto' }}>
                                    {loading ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : error ? (
                                        <Box sx={{ p: 2 }}>
                                            <Alert severity="error">
                                                {error}
                                            </Alert>
                                        </Box>
                                    ) : documents.length === 0 ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                No documents found
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <List sx={{ p: 0 }}>
                                            {documents.map((doc, index) => (
                                                <React.Fragment key={doc.id}>
                                                    <ListItem disablePadding>
                                                        <ListItemButton
                                                            selected={
                                                                selectedDocument?.id ===
                                                                doc.id
                                                            }
                                                            onClick={() =>
                                                                handleDocumentSelect(
                                                                    doc
                                                                )
                                                            }
                                                            sx={{
                                                                '&.Mui-selected':
                                                                    {
                                                                        backgroundColor:
                                                                            'primary.light',
                                                                        '&:hover':
                                                                            {
                                                                                backgroundColor:
                                                                                    'primary.light',
                                                                            },
                                                                    },
                                                            }}
                                                        >
                                                            <ListItemIcon
                                                                sx={{
                                                                    minWidth: 44,
                                                                }}
                                                            >
                                                                <DocumentThumbnail
                                                                    document={
                                                                        doc
                                                                    }
                                                                    size={36}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'flex-start',
                                                                            gap: 1,
                                                                            mb: 0.5,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                maxWidth:
                                                                                    '40ch',
                                                                                fontWeight:
                                                                                    'bold',
                                                                                whiteSpace:
                                                                                    'normal',
                                                                                overflowWrap:
                                                                                    'break-word',
                                                                                wordBreak:
                                                                                    'break-word',
                                                                            }}
                                                                        >
                                                                            {doc.title ||
                                                                                doc.filename ||
                                                                                'Untitled Document'}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={
                                                                                doc.file_type ||
                                                                                'Unknown'
                                                                            }
                                                                            size="small"
                                                                            color={getFileTypeColor(
                                                                                doc.file_type
                                                                            )}
                                                                            variant="outlined"
                                                                        />
                                                                    </Box>
                                                                }
                                                                secondary={
                                                                    <Box>
                                                                        <Box
                                                                            sx={{
                                                                                display:
                                                                                    'flex',
                                                                                alignItems:
                                                                                    'center',
                                                                                gap: 1,
                                                                                mt: 0.5,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {formatFileSize(
                                                                                    doc.file_size
                                                                                )}
                                                                            </Typography>
                                                                            {doc.rating && (
                                                                                <Box
                                                                                    sx={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: 0.5,
                                                                                    }}
                                                                                >
                                                                                    <Star
                                                                                        sx={{
                                                                                            fontSize: 12,
                                                                                            color: 'warning.main',
                                                                                        }}
                                                                                    />
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="text.secondary"
                                                                                    >
                                                                                        {
                                                                                            doc.rating
                                                                                        }
                                                                                        /5
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        {isNonTerminalStatus(
                                                                            doc.status
                                                                        ) ||
                                                                        doc.status ===
                                                                            'failed' ? (
                                                                            <Box
                                                                                sx={{
                                                                                    mt: 0.5,
                                                                                }}
                                                                            >
                                                                                <DocumentStatusChip
                                                                                    status={
                                                                                        doc.status
                                                                                    }
                                                                                    errorMessage={
                                                                                        doc.error_message
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                        ) : null}
                                                                    </Box>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                    {index <
                                                        documents.length -
                                                            1 && <Divider />}
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Document Detail Panel */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ height: '100%', overflow: 'auto' }}>
                                {selectedDocument ? (
                                    <Box sx={{ p: 3 }}>
                                        {/* Document Header */}
                                        <Box
                                            sx={{
                                                mb: 3,
                                                pb: 2,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    mb: 2,
                                                }}
                                            >
                                                <DocumentThumbnail
                                                    document={selectedDocument}
                                                    size={40}
                                                />
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {isEditingTitle ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'flex-start',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <TextField
                                                                value={
                                                                    titleDraft
                                                                }
                                                                onChange={(e) =>
                                                                    setTitleDraft(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                                onKeyDown={(
                                                                    e
                                                                ) => {
                                                                    if (
                                                                        e.key ===
                                                                        'Enter'
                                                                    ) {
                                                                        e.preventDefault();
                                                                        handleSaveTitle();
                                                                    } else if (
                                                                        e.key ===
                                                                        'Escape'
                                                                    ) {
                                                                        handleCancelEditTitle();
                                                                    }
                                                                }}
                                                                size="small"
                                                                autoFocus
                                                                fullWidth
                                                                disabled={
                                                                    savingTitle
                                                                }
                                                                error={Boolean(
                                                                    titleError
                                                                )}
                                                                helperText={
                                                                    titleError
                                                                }
                                                            />
                                                            <Tooltip title="Save">
                                                                <span>
                                                                    <IconButton
                                                                        color="primary"
                                                                        onClick={
                                                                            handleSaveTitle
                                                                        }
                                                                        disabled={
                                                                            savingTitle ||
                                                                            !titleDraft.trim()
                                                                        }
                                                                    >
                                                                        <Save />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Cancel">
                                                                <span>
                                                                    <IconButton
                                                                        onClick={
                                                                            handleCancelEditTitle
                                                                        }
                                                                        disabled={
                                                                            savingTitle
                                                                        }
                                                                    >
                                                                        <Close />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Box>
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'flex-start',
                                                                gap: 1,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="h5"
                                                                gutterBottom
                                                                sx={{
                                                                    mb: 0,
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                    maxWidth:
                                                                        '40ch',
                                                                    overflowWrap:
                                                                        'break-word',
                                                                    wordBreak:
                                                                        'break-word',
                                                                }}
                                                            >
                                                                {selectedDocument.title ||
                                                                    selectedDocument.filename ||
                                                                    'Untitled Document'}
                                                            </Typography>
                                                            <Tooltip title="Rename Document">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={
                                                                        handleStartEditTitle
                                                                    }
                                                                    sx={{
                                                                        flexShrink: 0,
                                                                    }}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <Tooltip title="View Document">
                                                        <IconButton color="primary">
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Download Document">
                                                        <span>
                                                            <IconButton
                                                                color="primary"
                                                                onClick={
                                                                    handleDownload
                                                                }
                                                                disabled={
                                                                    downloading
                                                                }
                                                            >
                                                                <Download />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Document">
                                                        <IconButton
                                                            color="error"
                                                            onClick={() => {
                                                                setDeleteError(
                                                                    null
                                                                );
                                                                setDeleteDialogOpen(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                    mb: 2,
                                                }}
                                            >
                                                <Chip
                                                    icon={<Article />}
                                                    label={
                                                        selectedDocument.file_type ||
                                                        'Unknown Type'
                                                    }
                                                    color={getFileTypeColor(
                                                        selectedDocument.file_type
                                                    )}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={<StorageIcon />}
                                                    label={formatFileSize(
                                                        selectedDocument.file_size
                                                    )}
                                                    variant="outlined"
                                                />
                                                {selectedDocument.rating && (
                                                    <Chip
                                                        icon={<Star />}
                                                        label={`${selectedDocument.rating}/5`}
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                )}
                                                <DocumentStatusChip
                                                    status={
                                                        selectedDocument.status
                                                    }
                                                    errorMessage={
                                                        selectedDocument.error_message
                                                    }
                                                    size="medium"
                                                />
                                            </Box>

                                            {/* Department Scope Section */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mr: 1,
                                                    }}
                                                >
                                                    <Business
                                                        sx={{
                                                            fontSize: 16,
                                                        }}
                                                    />
                                                    Department:
                                                </Typography>
                                                {getDepartmentNames(
                                                    selectedDocument
                                                ).length > 0 ? (
                                                    getDepartmentNames(
                                                        selectedDocument
                                                    ).map((name, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={name}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                mb: 0.5,
                                                            }}
                                                        />
                                                    ))
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        No department assigned
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Document Details */}
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Person />
                                                            Upload Information
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection:
                                                                    'column',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    Uploaded By
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {selectedDocument
                                                                        .uploaded_by
                                                                        ?.username ||
                                                                        'Unknown User'}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    Upload Date
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        display:
                                                                            'flex',
                                                                        alignItems:
                                                                            'center',
                                                                        gap: 0.5,
                                                                    }}
                                                                >
                                                                    <CalendarToday
                                                                        sx={{
                                                                            fontSize: 14,
                                                                        }}
                                                                    />
                                                                    {formatDate(
                                                                        selectedDocument.uploaded_at
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            {selectedDocument.additional_info && (
                                                <Grid item xs={12}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Typography
                                                                variant="h6"
                                                                gutterBottom
                                                            >
                                                                Additional
                                                                Information
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace:
                                                                        'pre-wrap',
                                                                }}
                                                            >
                                                                {
                                                                    selectedDocument.additional_info
                                                                }
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            )}

                                            {/* Document Preview/Content Area */}
                                            <Grid item xs={12}>
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography
                                                            variant="h6"
                                                            gutterBottom
                                                        >
                                                            Document Preview
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                minHeight: 300,
                                                                border: 1,
                                                                borderColor:
                                                                    'divider',
                                                                borderRadius: 1,
                                                                p: 2,
                                                                backgroundColor:
                                                                    'grey.50',
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'center',
                                                                flexDirection:
                                                                    'column',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <DocumentPreview
                                                                document={
                                                                    selectedDocument
                                                                }
                                                            />
                                                        </Box>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={
                                                                <Download />
                                                            }
                                                            onClick={
                                                                handleDownload
                                                            }
                                                            disabled={
                                                                downloading
                                                            }
                                                            sx={{ mt: 2 }}
                                                        >
                                                            {downloading
                                                                ? 'Downloading...'
                                                                : 'Download Document'}
                                                        </Button>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}
                                    >
                                        <Description
                                            sx={{
                                                fontSize: 80,
                                                color: 'grey.300',
                                            }}
                                        />
                                        <Typography
                                            variant="h6"
                                            color="text.secondary"
                                        >
                                            Select a document to view details
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            textAlign="center"
                                        >
                                            Choose a document from the list on
                                            the left to see its detailed
                                            information
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                            <Dialog
                                open={deleteDialogOpen}
                                onClose={() =>
                                    !deleting && setDeleteDialogOpen(false)
                                }
                            >
                                <DialogTitle>Delete Document</DialogTitle>
                                <DialogContent>
                                    <DialogContentText>
                                        Are you sure you want to delete{' '}
                                        <strong>
                                            {selectedDocument?.title ||
                                                selectedDocument?.filename ||
                                                'this document'}
                                        </strong>
                                        ? This action cannot be undone.
                                    </DialogContentText>
                                    {deleteError && (
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            sx={{ mt: 2 }}
                                        >
                                            {deleteError}
                                        </Typography>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={() =>
                                            setDeleteDialogOpen(false)
                                        }
                                        disabled={deleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleDeleteDocument}
                                        color="error"
                                        variant="contained"
                                        disabled={deleting}
                                    >
                                        {deleting ? 'Deleting...' : 'Delete'}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Box>
    );
}
